import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi, type NotificationCampaign, type NotificationCreateInput } from "@/lib/api";
import { NOTIFICATION_POLL_INTERVAL_MS, hasActivePolling } from "@/lib/utils/notificationProcessing";
import { queryKeys } from "./keys";

export function useNotifications() {
  return useQuery({
    queryKey: queryKeys.notifications.list,
    queryFn: notificationsApi.listNotifications,
    // Poll only while backend-reported jobs are still pending/processing.
    // Terminal backend states stop polling immediately.
    refetchInterval: (query) => {
      const data = query.state.data as NotificationCampaign[] | undefined;
      return hasActivePolling(data) ? NOTIFICATION_POLL_INTERVAL_MS : false;
    },
  });
}

function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list });
}

export function useCreateNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (payload: NotificationCreateInput) => notificationsApi.createNotification(payload),
    onSuccess: () => void invalidate(),
  });
}

export function useSendNotificationNow() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.sendNotificationNow(id),
    onSuccess: () => void invalidate(),
  });
}

export function useCancelNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.cancelNotification(id),
    onSuccess: () => void invalidate(),
  });
}
